import axios from 'axios';

// =============================================================================
// API Configuration
// =============================================================================
export const API_CONFIG = {
  // BASE_URL: import.meta.env.VITE_BACKEND_URL || 'https://krbr-api-357422789054.asia-south1.run.app/api/v1',
  // BASE_URL: 'https://krbr-api-357422789054.asia-south1.run.app',
  // BASE_URL: 'http://localhost:8000',
  BASE_URL: 'https://karobar-backend.onrender.com',
  API_PREFIX: '/api/v1',
  TIMEOUT: parseInt(import.meta.env.VITE_API_TIMEOUT) || 30000,
};


// =============================================================================
// Token Management
// =============================================================================
const TOKEN_KEYS = {
  ACCESS_TOKEN: 'karobar_access_token',
  REFRESH_TOKEN: 'karobar_refresh_token',
  TOKEN_EXPIRY: 'karobar_token_expiry',
};

export const tokenManager = {
  getAccessToken: () => localStorage.getItem(TOKEN_KEYS.ACCESS_TOKEN),
  getRefreshToken: () => localStorage.getItem(TOKEN_KEYS.REFRESH_TOKEN),

  setTokens: ({ access_token, refresh_token, expires_in }) => {
    localStorage.setItem(TOKEN_KEYS.ACCESS_TOKEN, access_token);
    if (refresh_token) {
      localStorage.setItem(TOKEN_KEYS.REFRESH_TOKEN, refresh_token);
    }
    if (expires_in) {
      const expiryTime = Date.now() + expires_in * 1000;
      localStorage.setItem(TOKEN_KEYS.TOKEN_EXPIRY, String(expiryTime));
    }
  },

  clearTokens: () => {
    localStorage.removeItem(TOKEN_KEYS.ACCESS_TOKEN);
    localStorage.removeItem(TOKEN_KEYS.REFRESH_TOKEN);
    localStorage.removeItem(TOKEN_KEYS.TOKEN_EXPIRY);
  },

  isTokenExpired: () => {
    const expiry = localStorage.getItem(TOKEN_KEYS.TOKEN_EXPIRY);
    if (!expiry) return true;
    // Consider token expired 60 seconds early to avoid edge cases
    return Date.now() >= parseInt(expiry) - 60000;
  },

  hasTokens: () => {
    return !!localStorage.getItem(TOKEN_KEYS.ACCESS_TOKEN) &&
           !!localStorage.getItem(TOKEN_KEYS.REFRESH_TOKEN);
  },
};

// =============================================================================
// Axios Instance
// =============================================================================
const apiClient = axios.create({
  baseURL: `${API_CONFIG.BASE_URL}${API_CONFIG.API_PREFIX}`,
  timeout: API_CONFIG.TIMEOUT,
  headers: {
    'Content-Type': 'application/json',
  },
});

// =============================================================================
// Request Interceptor — attach Authorization header
// =============================================================================
apiClient.interceptors.request.use(
  (config) => {
    const token = tokenManager.getAccessToken();
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  (error) => Promise.reject(error)
);

// =============================================================================
// Response Interceptor — handle token refresh on 401
// =============================================================================
let isRefreshing = false;
let failedQueue = [];

const processQueue = (error, token = null) => {
  failedQueue.forEach((prom) => {
    if (error) {
      prom.reject(error);
    } else {
      prom.resolve(token);
    }
  });
  failedQueue = [];
};

apiClient.interceptors.response.use(
  (response) => response,
  async (error) => {
    const originalRequest = error.config;

    // If the error is 401 and we haven't already retried
    if (error.response?.status === 401 && !originalRequest._retry) {
      // Don't retry auth endpoints themselves
      if (originalRequest.url?.includes('/auth/login') ||
          originalRequest.url?.includes('/auth/refresh')) {
        return Promise.reject(error);
      }

      if (isRefreshing) {
        // Queue this request while token is being refreshed
        return new Promise((resolve, reject) => {
          failedQueue.push({ resolve, reject });
        })
          .then((token) => {
            originalRequest.headers.Authorization = `Bearer ${token}`;
            return apiClient(originalRequest);
          })
          .catch((err) => Promise.reject(err));
      }

      originalRequest._retry = true;
      isRefreshing = true;

      const refreshToken = tokenManager.getRefreshToken();
      if (!refreshToken) {
        isRefreshing = false;
        tokenManager.clearTokens();
        localStorage.removeItem('auth-storage');
        window.location.href = '/login';
        return Promise.reject(error);
      }

      try {
        const response = await axios.post(
          `${API_CONFIG.BASE_URL}${API_CONFIG.API_PREFIX}/auth/refresh`,
          { refresh_token: refreshToken },
          { headers: { 'Content-Type': 'application/json' } }
        );

        const { access_token, expires_in } = response.data.data;
        tokenManager.setTokens({ access_token, expires_in });

        processQueue(null, access_token);

        originalRequest.headers.Authorization = `Bearer ${access_token}`;
        return apiClient(originalRequest);
      } catch (refreshError) {
        processQueue(refreshError, null);
        tokenManager.clearTokens();
        localStorage.removeItem('auth-storage');
        window.location.href = '/login';
        return Promise.reject(refreshError);
      } finally {
        isRefreshing = false;
      }
    }

    return Promise.reject(error);
  }
);

// =============================================================================
// Helper Functions
// =============================================================================

/**
 * Extract data from the standard API envelope { success, data, ... }
 * Throws on success: false
 */
export const extractData = (response) => {
  const body = response.data;
  if (body.success === false) {
    const errMsg = body.error?.message || body.message || 'API request failed';
    const err = new Error(errMsg);
    err.code = body.error?.code;
    throw err;
  }
  return body.data;
};

/**
 * Standard error handler — extracts human-readable message from API or network errors
 */
export const handleApiError = (error) => {
  if (error.response) {
    // Server responded with an error status
    const body = error.response.data;
    if (body?.error?.message) return body.error.message;
    if (body?.detail) return typeof body.detail === 'string' ? body.detail : JSON.stringify(body.detail);
    if (body?.message) return body.message;
    return `Server error (${error.response.status})`;
  }
  if (error.request) {
    return 'Network error. Please check your connection.';
  }
  return error.message || 'An unexpected error occurred';
};

export { apiClient };
export default apiClient;
